import { USER_API_URL } from '@repo/editor/src/api/constants';
import { getAccessToken } from '@repo/editor/src/api/user';
import { fetchRetry } from '@repo/editor/src/utils/fetchRetry';

import { Comment, CommentContent } from './types/Comment';

// eslint-disable-next-line @typescript-eslint/ban-types
export type GenericResponse<T = {}> = { error: string } | ({ error: null } & T);

export type CommentsResponse = GenericResponse<{
  comments: Comment[];
  total: number;
}>;
export type CommentResponse = GenericResponse<{ data: Comment }>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const getComments = async ({
    postId,
    take,
    signal,
  }: {
    postId: string;
    take: number;
    signal: AbortSignal;
  }): Promise<CommentsResponse> => {
    const resp = await fetchRetry(
      `${url}/comments/index/${postId}?take=${take}`,
      { signal }
    );

    try {
      const { total, results } = await resp.json();
      return { comments: results, error: null, total };
    } catch (_err) {
      return {
        error: 'Failed to load comments.',
      };
    }
  };

  const getReplies = async ({
    commentId,
    take,
    signal,
  }: {
    commentId: string;
    take: number;
    signal: AbortSignal;
  }): Promise<CommentsResponse> => {
    const resp = await fetchRetry(
      `${url}/comments/replies/${commentId}?take=${take}`,
      { signal }
    );

    try {
      const { total, results } = await resp.json();
      return { comments: results, error: null, total };
    } catch (_err) {
      return {
        error: 'Failed to load replies.',
      };
    }
  };

  const postComment = async ({
    postId,
    commentId,
    content,
  }: {
    postId: string;
    commentId?: string;
    content: CommentContent[];
  }): Promise<CommentResponse> => {
    const response = await fetchRetry(`${url}/comments/create`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ postId, commentId, content }),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.id) {
        return { error: 'Failed to post comment.' };
      }

      return { data: responseJson, error: null };
    } catch (_error) {
      return { error: 'Failed to post comment.' };
    }
  };
  const deleteComment = async ({
    commentId,
  }: {
    commentId: string;
  }): Promise<GenericResponse> => {
    const response = await fetchRetry(`${url}/comments/delete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${commentId}`),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.success) {
        return { error: 'Failed to delete the comment.' };
      }

      return { error: null };
    } catch (_error) {
      return { error: 'Failed to delete the comment.' };
    }
  };

  return { getComments, getReplies, postComment, deleteComment };
};

export default createAPI({ url: USER_API_URL });
